import React, { useEffect, useRef, useState } from "react";
import { KeylineGrid, KeylineGridItem } from "web/react/components/keyline-grid";
import {
    ProductCardImage,
    ProductCardWishlist,
    ProductDetails,
    ProductPrice,
} from "web/react/components/product-card/react/emo";
import ProductCard from "web/react/components/product-card/react/product-card";
import { RelatedProductsShowMore } from "web/react/components/related-products-show-more/related-products-show-more";
import { HStack } from "web/react/emo/h-stack";
import { Heading } from "web/react/emo/heading";
import { VStack } from "web/react/emo/v-stack";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useIntersectionObserver } from "web/react/hooks/use-intersection-observer/use-intersection-observer";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { languageIsEnglish } from "web/script/modules/language";
import { RelatedProductsAreaSectionSerializer } from "web/types/serializers";
import * as styles from "./more-like-this-grid.css";

interface MoreLikeThisGridProps {
    section: RelatedProductsAreaSectionSerializer;
    isInStock: boolean;
    retailerSlug?: string | null;
}

export function MoreLikeThisGrid({
    section,
    isInStock,
    retailerSlug = null,
}: MoreLikeThisGridProps): React.ReactElement {
    const analyticsEventLabel = isInStock ? "is_in_stock_related" : "oos_in_stock_related";

    const [sentAnalytics, setSentAnalytics] = useState(false);

    const moreLikeThisRef = useRef<HTMLDivElement | null>(null);

    const { isMobileViewport } = useDomViewport();
    const [isVisible] = useIntersectionObserver(
        {
            rootMargin: "0px",
            threshold: 0.25,
            once: true,
        },
        moreLikeThisRef as any
    );

    useEffect(() => {
        if (isVisible && !sentAnalytics) {
            setSentAnalytics(true);
            analytics.event("product_card", "impression", analyticsEventLabel);
        }
    }, [isVisible, isMobileViewport, analyticsEventLabel, sentAnalytics]);

    return (
        <div id="relatedproducts" ref={moreLikeThisRef}>
            <VStack spacing="lg">
                <Heading textStyle={{ sm: "title-3", md: "title-2" }}>
                    {gettext("product.buy_area.more_like_this.label")}
                </Heading>
                {section.related_products && (
                    <KeylineGrid maxColumns={5}>
                        {section.related_products.map((product, i) => {
                            return (
                                <KeylineGridItem key={`${product.uid}-${i}`}>
                                    <ProductCard product={product} dataTestId="product-card">
                                        <VStack spacing="xxs">
                                            <ProductCardImage
                                                analyticsEventLabel={"is_pdp_similar_product"}
                                                shouldRenderLeadLinkInImage
                                            />
                                            <VStack spacing="xxxs">
                                                <HStack spacing="none" justify="space-between">
                                                    <ProductPrice size="sm" />
                                                    {languageIsEnglish() && <ProductCardWishlist />}
                                                </HStack>
                                                <ProductDetails
                                                    analyticsEventLabel={"is_pdp_similar_product"}
                                                />
                                            </VStack>
                                        </VStack>
                                    </ProductCard>
                                </KeylineGridItem>
                            );
                        })}
                    </KeylineGrid>
                )}
                {section.view_more_url && (
                    <div className={styles.showMoreContainer}>
                        <RelatedProductsShowMore
                            view_more_url={section.view_more_url}
                            product_is_instock={isInStock}
                            retailerSlug={retailerSlug}
                            isRedesign
                        />
                    </div>
                )}
            </VStack>
        </div>
    );
}
